import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen, Rocket, Users, Beaker, MessageSquare, Search,
  FileText, Shield, Zap, ArrowRight, ChevronRight, Globe,
  Code, Database, Key, Layers, Settings, Terminal,
  GraduationCap, Lightbulb, Clock, CheckCircle2, ExternalLink,
  Hash, Cpu, Sparkles
} from 'lucide-react';
import ResourcesLayout from './ResourcesLayout';
import './DocumentationPage.css';

const DocumentationPage = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const isVisible = (id) => visibleSections.has(id);

  const gettingStartedSteps = [
    {
      step: '01',
      icon: Rocket,
      title: 'Create Your Account',
      description: 'Sign up with your institutional email or Google account. Complete your research profile.',
      time: '2 min',
    },
    {
      step: '02',
      icon: Search,
      title: 'Explore Research Papers',
      description: 'Search 200M+ papers using AI-powered semantic search. Save, cite, and share instantly.',
      time: '5 min',
    },
    {
      step: '03',
      icon: Beaker,
      title: 'Set Up Your Lab',
      description: 'Create a Research Lab with chat, tasks, files, library, and calendar — all in one space.',
      time: '3 min',
    },
    {
      step: '04',
      icon: Users,
      title: 'Collaborate & Publish',
      description: 'Invite collaborators, join communities, share findings, and accelerate your research.',
      time: '5 min',
    },
  ];

  const platformGuides = [
    {
      slug: 'paper-search-discovery',
      icon: Search,
      title: 'Paper Search & Discovery',
      description: 'Master AI-powered search, filters, saved papers, citations, and personalized recommendations.',
      sections: 4,
      tag: 'Core',
      tagColor: 'green',
      category: 'platform',
    },
    {
      slug: 'research-labs',
      icon: Beaker,
      title: 'Research Labs',
      description: 'Create labs, manage members, use chat, tasks board, file storage, shared library, and calendar.',
      sections: 4,
      tag: 'Popular',
      tagColor: 'blue',
      category: 'platform',
    },
    {
      slug: 'communities',
      icon: Users,
      title: 'Communities',
      description: 'Join communities, create threads, participate in discussions, moderate content.',
      sections: 4,
      tag: 'Essential',
      tagColor: 'purple',
      category: 'platform',
    },
    {
      slug: 'research-feed',
      icon: FileText,
      title: 'Research Feed',
      description: 'Publish posts, share research, create polls, questions, milestones, and engage with peers.',
      sections: 3,
      tag: null,
      tagColor: null,
      category: 'platform',
    },
    {
      slug: 'messaging-e2ee',
      icon: MessageSquare,
      title: 'Messaging & E2EE',
      description: 'Direct messaging with end-to-end encryption, file sharing, reactions, and jump-to-message.',
      sections: 3,
      tag: 'Secure',
      tagColor: 'green',
      category: 'platform',
    },
    {
      slug: 'courses-workshops',
      icon: GraduationCap,
      title: 'Courses & Workshops',
      description: 'Browse courses, attend workshops, manage enrollments, submit feedback, and track progress.',
      sections: 3,
      tag: 'New',
      tagColor: 'amber',
      category: 'platform',
    },
  ];

  const developerResources = [
    {
      slug: 'api-overview',
      icon: Code,
      title: 'API Overview',
      description: 'RESTful architecture, authentication, rate limiting, and response formats.',
      sections: 3,
      category: 'developer',
    },
    {
      slug: 'authentication',
      icon: Key,
      title: 'Authentication',
      description: 'JWT-based auth, OAuth 2.0, token refresh, and session management.',
      sections: 3,
      category: 'developer',
    },
    {
      slug: 'endpoints-reference',
      icon: Database,
      title: 'Endpoints Reference',
      description: 'Complete endpoint documentation for papers, rooms, communities, feed, and messaging.',
      sections: 4,
      category: 'developer',
    },
    {
      slug: 'websocket-events',
      icon: Terminal,
      title: 'WebSocket Events',
      description: 'Real-time socket events for messaging, rooms, communities, and notifications.',
      sections: 3,
      category: 'developer',
    },
    {
      slug: 'encryption-security',
      icon: Shield,
      title: 'Encryption & Security',
      description: 'End-to-end encryption with ECDH P-256 key exchange and AES-256-GCM cipher.',
      sections: 3,
      category: 'developer',
    },
    {
      slug: 'webhooks-integrations',
      icon: Layers,
      title: 'Webhooks & Integrations',
      description: 'Event-driven webhooks for community activity, research updates, and task changes.',
      sections: 3,
      category: 'developer',
    },
  ];

  const quickLinks = [
    { icon: Rocket, label: 'Quick Start', to: '#doc-getting-started' },
    { icon: Settings, label: 'Account Settings', to: '/settings' },
    { icon: Globe, label: 'Platform Status', to: '/help' },
    { icon: Lightbulb, label: 'Feature Requests', to: '/contact' },
    { icon: Cpu, label: 'API Reference', to: '/docs/api-overview' },
    { icon: Sparkles, label: "What's New", to: '/blog' },
  ];

  const tabs = [
    { id: 'all', label: 'All Docs' },
    { id: 'platform', label: 'Platform' },
    { id: 'developer', label: 'Developer' },
  ];

  const allGuides = [...platformGuides, ...developerResources];
  const filteredGuides = activeTab === 'all'
    ? allGuides
    : allGuides.filter(g => g.category === activeTab);

  return (
    <ResourcesLayout>
      {/* ── Hero ── */}
      <section className="doc-hero">
        <div className="doc-hero-inner">
          <h1 className="doc-hero-title">
            Everything you need to <span className="doc-hero-gradient">build & research</span>
          </h1>
          <p className="doc-hero-subtitle">
            From quick start guides to advanced API documentation — 
            find answers to any question about the platform.
          </p>

          <div className="doc-quick-links">
            {quickLinks.map((item, i) => (
              <Link key={i} to={item.to} className="doc-quick-link">
                <item.icon size={14} />
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Getting Started ── */}
      <section
        id="doc-getting-started"
        className={`doc-section ${isVisible('doc-getting-started') ? 'doc-visible' : ''}`}
        ref={(el) => (sectionRefs.current['doc-getting-started'] = el)}
      >
        <div className="doc-section-inner">
          <div className="doc-section-header">
            <span className="doc-section-number">01</span>
            <div>
              <h2 className="doc-section-title">Getting Started</h2>
              <p className="doc-section-subtitle">Go from zero to collaborating in under 15 minutes</p>
            </div>
          </div>

          <div className="doc-steps">
            {gettingStartedSteps.map((step, i) => (
              <div key={i} className="doc-step" style={{ '--step-delay': `${i * 80}ms` }}>
                <div className="doc-step-marker">
                  <div className="doc-step-num">{step.step}</div>
                  {i < gettingStartedSteps.length - 1 && <div className="doc-step-line" />}
                </div>
                <div className="doc-step-body">
                  <div className="doc-step-icon">
                    <step.icon size={18} />
                  </div>
                  <h3 className="doc-step-title">{step.title}</h3>
                  <p className="doc-step-desc">{step.description}</p>
                  <span className="doc-step-time">
                    <Clock size={12} />
                    {step.time}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Guides & Reference ── */}
      <section
        id="doc-guides"
        className={`doc-section doc-section--alt ${isVisible('doc-guides') ? 'doc-visible' : ''}`}
        ref={(el) => (sectionRefs.current['doc-guides'] = el)}
      >
        <div className="doc-section-inner">
          <div className="doc-section-header">
            <span className="doc-section-number">02</span>
            <div>
              <h2 className="doc-section-title">Guides & Reference</h2>
              <p className="doc-section-subtitle">Deep dives into every feature and API endpoint</p>
            </div>
          </div>

          <div className="doc-tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`doc-tab ${activeTab === tab.id ? 'doc-tab--active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="doc-guides-grid">
            {filteredGuides.map((guide, i) => (
              <Link
                key={guide.slug}
                to={`/docs/${guide.slug}`}
                className="doc-guide-card"
                style={{ '--card-delay': `${i * 60}ms` }}
              >
                <div className="doc-guide-card-top">
                  <div className={`doc-guide-icon ${guide.category === 'developer' ? 'doc-guide-icon--dev' : ''}`}>
                    <guide.icon size={20} />
                  </div>
                  {guide.tag && (
                    <span className={`doc-guide-tag doc-guide-tag--${guide.tagColor}`}>
                      {guide.tag}
                    </span>
                  )}
                </div>
                <h3 className="doc-guide-title">{guide.title}</h3>
                <p className="doc-guide-desc">{guide.description}</p>
                <div className="doc-guide-footer">
                  {guide.sections && (
                    <span className="doc-guide-count">
                      <FileText size={12} />
                      {guide.sections} sections
                    </span>
                  )}
                  <span className="doc-guide-link">
                    {guide.category === 'developer' ? 'View Docs' : 'Read Guide'}
                    <ArrowRight size={13} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="doc-cta">
        <div className="doc-cta-inner">
          <span className="doc-cta-badge">
            <Zap size={14} />
            Need Help?
          </span>
          <h2 className="doc-cta-title">Can't find what you need?</h2>
          <p className="doc-cta-subtitle">
            Our support team is here to help. Reach out through our Help Center 
            or join the community for peer support.
          </p>
          <div className="doc-cta-actions">
            <Link to="/help" className="doc-btn doc-btn--primary">
              Visit Help Center
              <ArrowRight size={16} />
            </Link>
            <Link to="/resources" className="doc-btn doc-btn--secondary">
              All Resources
            </Link>
          </div>
        </div>
      </section>
    </ResourcesLayout>
  );
};

export default DocumentationPage;
